import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ErrorResponse } from '../../core/models/error-response.model';
import { AuthService } from '../../core/services/auth.service';
import { AuthLoginResponseModel } from '../models/auth-login-response-model';
import { AuthLoginModel } from '../models/auth-login.model';
import { AuthApiService } from './auth-api.service';
import { AuthState } from './auth.state';

@Injectable({
	providedIn: 'root',
})
export class AuthFacade {
	isLoading$: Observable<boolean>;
	error$: Observable<ErrorResponse | null>;

	constructor(
		private authApiService: AuthApiService,
		private authService: AuthService,
		private authState: AuthState
	) {
		this.isLoading$ = this.authState.isLoading$();
		this.error$ = this.authState.error$();
	}

	login$(data: AuthLoginModel): Observable<AuthLoginResponseModel> {
		this.authState.setIsLoading(true);

		// reset - these are not relevant to the login action
		this.authState.setError(null);

		// do not reset loading, guards are async so loading state should be preserved until component is destroyed
		return this.authApiService.login$(data).pipe(
			catchError((response) => {
				this.authState.setIsLoading(false);

				return throwError(response);
			}),
			finalize(() => {
				this.authState.setIsLoading(false);
			})
		);
	}

	getRecaptchaKey$(): Observable<string> {
		return this.authApiService.getRecaptchaKey$();
	}

	refreshToken$(): Observable<AuthLoginResponseModel> {
		return this.authApiService.refreshToken$();
	}

	setNewPassword$(newPassword: { oldPassword: string; newPassword: string }): Observable<AuthLoginResponseModel> {
		this.authState.setIsLoading(true);
		this.authState.setError(null);

		return this.authApiService.setNewPasword$(newPassword).pipe(finalize(() => this.authState.setIsLoading(false)));
	}

	forgottenPassword$(email: string): Observable<null> {
		this.authState.setIsLoading(true);
		this.authState.setError(null);

		return this.authApiService.forgottenPassword$(email).pipe(finalize(() => this.authState.setIsLoading(false)));
	}

	resetPassword$(newPassword: { newPassword: string; confirmPassword: string }): Observable<null> {
		this.authState.setIsLoading(true);
		this.authState.setError(null);

		return this.authApiService.resetPassword$(newPassword).pipe(finalize(() => this.authState.setIsLoading(false)));
	}

	setError(error: ErrorResponse | null): void {
		this.authState.setError(error);
	}

	logout(): void {
		this.authService.logout();
	}

	getAccessToken(): string | null {
		return this.authService.getAccessToken();
	}

	getRefreshToken(): string | null {
		return this.authService.getRefreshToken();
	}

	isAccessTokenExpired(): boolean {
		return this.authService.isAccessTokenExpired();
	}

	getOAuthToken(): string {
		return this.authService.getOAuthToken();
	}

	getLoginPath(): string {
		return this.authApiService.getLoginPath();
	}

	resetState(): void {
		this.authState.setIsLoading(false);
		this.authState.setError(null);
	}

	setAccessToken(token: string): void {
		this.authService.setAccessToken(token);
	}
}
